import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Dumbbell,
  Users,
  User,
  LogOut,
  Menu,
  X,
  FolderKanban
} from 'lucide-react';
import { Logo } from './Logo';
import './Layout.css';

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
  };

  const menuItems = React.useMemo(() => {
    if (!user) return [];

    const items = [];

    if (user.role === 'user') {
      items.push(
        { path: '/calendar', label: t('calendar'), icon: Calendar },
        { path: '/my-trainings', label: t('myTrainings'), icon: Dumbbell }
      );
    }

    if (user.role === 'coach') {
      items.push(
        { path: '/coach/calendar', label: t('calendar'), icon: Calendar },
        { path: '/coach/trainings', label: t('trainings'), icon: Dumbbell },
        { path: '/coach/trainees', label: t('myStudents'), icon: Users },
        { path: '/coach/exercises', label: t('exercises'), icon: Dumbbell },
        { path: '/coach/categories', label: t('categories'), icon: FolderKanban }
      );
    }

    if (user.role === 'admin') {
      items.push(
        { path: '/admin/trainings', label: t('trainings'), icon: Dumbbell },
        { path: '/admin/exercises', label: t('exercises'), icon: Dumbbell },
        { path: '/admin/categories', label: t('categories'), icon: Dumbbell },
        { path: '/admin/users', label: t('users'), icon: Users }
      );
    }

    items.push({ path: '/profile', label: t('profile'), icon: User });

    return items;
  }, [user, t]);

  return (
    <div className="layout">
      {/* Header para móvil */}
      <header className="mobile-header">
        <div className="mobile-header-content">
          <Logo className="header-title" />
          <div className="mobile-header-actions">
            <button onClick={toggleLanguage} className="lang-btn">
              {i18n.language.toUpperCase()}
            </button>
            <button
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="mobile-nav">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
            <button onClick={handleLogout} className="mobile-logout-btn">
              <LogOut size={20} />
              <span>{t('logout')}</span>
            </button>
          </nav>
        )}
      </header>

      {/* Sidebar para desktop */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <Logo className="sidebar-title" />
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`sidebar-nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          {user && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user.name}</div>
              <div className="sidebar-user-email">{user.email}</div>
            </div>
          )}
          <button onClick={toggleLanguage} className="sidebar-lang-btn">
            {i18n.language.toUpperCase()}
          </button>
          <button onClick={handleLogout} className="sidebar-logout-btn">
            <LogOut size={20} />
            <span>{t('logout')}</span>
          </button>
        </div>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
};
